/**
 * Career experience duration maths, shared by every place that sums up a
 * staff/coach career timeline.
 *
 * The rule this module exists to enforce: a station without a usable start date
 * is NOT counted. It is never back-filled with a guessed date, because that
 * invents work history for a real person on their own profile.
 *
 * Station shapes in this codebase (all supported here):
 *   - profile form / ExperienceSection:  { from, to }        ("YYYY-MM", "present")
 *   - saved payload / StaffProfile:      { start, end }      ("YYYY-MM", end: null = until today)
 *   - API read models:                   { start_date, end_date }
 *   - legacy camelCase:                  { startDate, endDate }
 *
 * `end` missing / null / "" / "present" means "until today" — that is how the
 * profile form writes an ongoing station (StaffProfile.js: end: to === "present" ? null : to)
 * and how the backend stores it. An end that is present but unparseable is NOT
 * treated as ongoing: we cannot verify it, so the station is skipped.
 */

const MONTH_ONLY = /^\d{4}-\d{2}$/;

const pickStart = (exp) =>
  exp.start_date != null && exp.start_date !== ""
    ? exp.start_date
    : exp.start != null && exp.start !== ""
    ? exp.start
    : exp.from != null && exp.from !== ""
    ? exp.from
    : exp.startDate;

const pickEnd = (exp) =>
  exp.end_date != null && exp.end_date !== ""
    ? exp.end_date
    : exp.end != null && exp.end !== ""
    ? exp.end
    : exp.to != null && exp.to !== ""
    ? exp.to
    : exp.endDate;

/** `null`/`""`/`"present"` on the end side means "still going on". */
export const isOngoingEnd = (value) =>
  value == null ||
  (typeof value === "string" && (value.trim() === "" || value.trim().toLowerCase() === "present"));

/**
 * Parse a career date. Accepts "YYYY-MM" (the month pickers write this),
 * "YYYY-MM-DD", and Date objects. Returns null when nothing usable is there —
 * never a substitute date.
 */
export const parseCareerDate = (value) => {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalised = MONTH_ONLY.test(trimmed) ? `${trimmed}-01` : trimmed;
  const parsed = new Date(normalised);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Sum up a list of career stations.
 *
 * A station is skipped (and reported via `skipped`) when
 *   - it has no usable start date,
 *   - its end date is present but unparseable,
 *   - its end lies before its start (broken data must never shrink the total).
 *
 * @returns {{totalMonths:number, years:number, months:number, counted:number, skipped:number, isComplete:boolean}}
 */
export const summariseCareerExperience = (items, { now = new Date() } = {}) => {
  const list = Array.isArray(items) ? items : [];

  let totalMonths = 0;
  let counted = 0;
  let skipped = 0;

  list.forEach((exp) => {
    if (!exp || typeof exp !== "object") {
      skipped += 1;
      return;
    }

    const start = parseCareerDate(pickStart(exp));
    if (!start) {
      // No start date -> no duration. We do not guess one.
      skipped += 1;
      return;
    }

    const rawEnd = pickEnd(exp);
    const end = isOngoingEnd(rawEnd) ? now : parseCareerDate(rawEnd);
    if (!end || end.getTime() < start.getTime()) {
      skipped += 1;
      return;
    }

    let months =
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (end.getDate() < start.getDate()) months -= 1;

    if (months > 0) totalMonths += months;
    counted += 1;
  });

  return {
    totalMonths,
    years: Math.floor(totalMonths / 12),
    months: totalMonths % 12,
    counted,
    skipped,
    isComplete: skipped === 0,
  };
};

/** "3 years, 2 months" — the long form used next to the "Total:" label. */
export const formatCareerDuration = (totalMonths) => {
  const safeTotal = Number.isFinite(totalMonths) && totalMonths > 0 ? Math.floor(totalMonths) : 0;
  const years = Math.floor(safeTotal / 12);
  const months = safeTotal % 12;

  if (years === 0 && months === 0) return "Less than 1 month";
  if (years === 0) return `${months} month${months !== 1 ? "s" : ""}`;
  if (months === 0) return `${years} year${years !== 1 ? "s" : ""}`;
  return `${years} year${years !== 1 ? "s" : ""}, ${months} month${months !== 1 ? "s" : ""}`;
};

/**
 * Honest footnote for a partial total: names how many stations were left out.
 * Returns null when the total covers everything.
 */
export const formatExcludedStationsNote = (skipped) => {
  if (!Number.isFinite(skipped) || skipped <= 0) return null;
  return skipped === 1
    ? "Excludes 1 entry with incomplete dates"
    : `Excludes ${skipped} entries with incomplete dates`;
};
